import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { AlertTriangle, Clock, ShieldCheck, Smartphone, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { inr, MERCHANT } from "@/lib/content";

const WINDOW = 120;
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export const MerchantCard = () => (
  <div data-testid="merchant-info-card" className="rounded-2xl border border-[#E7E2DB] bg-[#FAF7F2] p-5 text-sm">
    <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#1B4332]"><ShieldCheck className="h-4 w-4" /> Merchant Information</p>
    <dl className="grid grid-cols-[110px_1fr] gap-y-1.5">
      <dt className="text-[#78716C]">Merchant Name</dt><dd data-testid="merchant-name" className="font-semibold">{MERCHANT.name}</dd>
      <dt className="text-[#78716C]">UPI ID</dt><dd data-testid="merchant-upi-id" className="break-all font-semibold">{MERCHANT.upi}</dd>
      <dt className="text-[#78716C]">Support Email</dt><dd><a href={`mailto:${MERCHANT.email}`} className="font-semibold text-[#701A24]">{MERCHANT.email}</a></dd>
    </dl>
  </div>
);

export default function UpiPayment({ order, onPaid, onRegenerate, regenerating }) {
  const [left, setLeft] = useState(WINDOW);
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const expired = left <= 0;
  const link = `upi://pay?pa=${MERCHANT.upi}&pn=${encodeURIComponent(MERCHANT.name)}&am=${order.total.toFixed(2)}&cu=INR&tn=${encodeURIComponent(`Kurtaani ${order.order_number}`)}`;

  useEffect(() => {
    setLeft(WINDOW);
    const t = setInterval(() => setLeft((s) => Math.max(s - 1, 0)), 1000);
    return () => clearInterval(t);
  }, [order.order_number]);
  useEffect(() => { if (expired) toast.error("QR code expired. Please refresh to generate a new QR code."); }, [expired]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { onPaid((await api.post(`/orders/${order.order_number}/payment`, { utr })).data); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="upi-payment-panel" className="mt-5 space-y-5">
      <MerchantCard />
      <div className="flex flex-col items-center rounded-3xl border-2 border-[#701A24] bg-white p-6 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#B85D43]">Scan & Pay {inr(order.total)}</p>
        <div className={`relative mt-4 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-[#E7E2DB] ${expired ? "opacity-30 blur-[3px]" : ""}`}>
          <QRCodeSVG data-testid="upi-qr-code" value={link} size={208} fgColor="#1C1917" level="M" />
        </div>
        <p className="mt-3 text-xs text-[#78716C]">Google Pay · PhonePe · Paytm · BHIM UPI</p>
        <p data-testid="qr-timer" className={`mt-4 flex items-center gap-2 font-semibold ${expired ? "text-red-600" : "text-[#1C1917]"}`}>
          <Clock className="h-4 w-4" /> {expired ? <span data-testid="qr-expired-status">EXPIRED</span> : <>QR Expires In: <span className="tabular-nums">{fmt(left)}</span></>}
        </p>
        {!expired && <a href={link} data-testid="open-upi-app-button" className="btn-outline mt-4 px-5 py-2.5 sm:hidden"><Smartphone className="h-4 w-4" /> Open UPI App</a>}
        {expired && (
          <div data-testid="qr-expired-alert" role="alert" className="mt-4 w-full rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <p className="flex items-center justify-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> This QR code has expired.</p>
            <p className="mt-1">Please refresh to generate a new QR code. Do not pay on the expired code.</p>
            <button type="button" data-testid="regenerate-qr-button" disabled={regenerating} onClick={onRegenerate} className="btn-primary mt-3 px-5 py-2.5"><RefreshCw className="h-4 w-4" /> {regenerating ? "Generating…" : "Refresh & Generate New QR"}</button>
          </div>
        )}
      </div>
      {!expired && (
        <div className="rounded-2xl bg-[#F4EFEA] p-5">
          <p className="text-sm font-semibold">Paid? Enter your UPI transaction ID</p>
          <p className="mt-1 text-xs text-[#78716C]">Find the 12-digit UTR / UPI Ref No. in your UPI app's payment details. We verify every payment before dispatch.</p>
          <div className="mt-3 flex gap-2">
            <input data-testid="utr-input" value={utr} onChange={(e) => setUtr(e.target.value.replace(/\D/g, "").slice(0, 12))} inputMode="numeric" placeholder="12-digit UTR" className="min-w-0 flex-1 rounded-full border border-[#E7E2DB] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#B85D43]" />
            <button type="button" data-testid="submit-utr-button" onClick={submit} disabled={busy || utr.length !== 12} className="btn-primary shrink-0 px-5 py-2.5">{busy ? "Submitting…" : "Confirm Payment"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
